import React, { useState, useEffect, useMemo } from "react";
import { getAllmobileApk } from "../Apis/mobileApis/mobileApi";
import { TableLoader } from "../Common/ShimmerLoader";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// "Live" / "Prod" from file names like DriverCentral_Live(1.0)(310826).apk
const getEnvironment = (fileName = "") => {
  const match = fileName.match(/_(live|prod|dev|uat|test)\b/i);
  if (!match) return null;
  const env = match[1].toLowerCase();
  return env.charAt(0).toUpperCase() + env.slice(1);
};

// "(1.0)" in the file name
const getVersionFromFile = (fileName = "") => {
  const match = fileName.match(/\((\d+(?:\.\d+)+)\)/);
  return match ? match[1] : null;
};

const formatDate = (date) =>
  date && !isNaN(date)
    ? `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`
    : null;

// "(220826)" in the file name -> 22 Aug 2026 (DDMMYY)
const getBuildFromFile = (fileName = "") => {
  const match = fileName.match(/\((\d{2})(\d{2})(\d{2})\)/);
  if (!match) return null;
  const [, dd, mm, yy] = match;
  return formatDate(new Date(2000 + Number(yy), Number(mm) - 1, Number(dd)));
};

const looksLikeFile = (value = "") =>
  /\.apk$|\(|_(live|prod|dev|uat|test)\b/i.test(value);

// The API puts the file name in appName / appVersion rather than fileName
const getFileName = (apk) =>
  [apk.fileName, apk.appName, apk.appVersion].find(
    (value) => value && looksLikeFile(value),
  ) || "";

// "DrainSlitCentral_Prod(1.0.0)(220826).apk" -> "Drain Slit"
const getDisplayName = (apk) => {
  if (apk.appName && !looksLikeFile(apk.appName)) return apk.appName;
  const base = (apk.appName || getFileName(apk))
    .replace(/\.apk$/i, "")
    .replace(/\s*\([^)]*\)/g, "")
    .replace(/_(live|prod|dev|uat|test)\b.*$/i, "")
    .replace(/Central$/, "")
    .trim();
  return base
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
};

const getVersion = (apk) => {
  if (/^\d+(\.\d+)+$/.test(apk.appVersion || "")) return apk.appVersion;
  return getVersionFromFile(getFileName(apk));
};

const getBuildDate = (apk) => {
  const raw =
    apk.buildDate ||
    apk.createdDate ||
    apk.createdOn ||
    apk.uploadedDate ||
    apk.lastModified;
  // appVersion is sometimes just the DDMMYY build code, e.g. "240826"
  const code = /^\d{6}$/.test(apk.appVersion || "")
    ? `(${apk.appVersion})`
    : "";
  return (
    (raw && formatDate(new Date(raw))) ||
    getBuildFromFile(getFileName(apk)) ||
    getBuildFromFile(code) ||
    null
  );
};

const MobileAPKs = () => {
  const [apkData, setApkData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [envFilter, setEnvFilter] = useState("All");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await getAllmobileApk();
        const list = Array.isArray(response?.data) ? response.data : [];
        setApkData(
          list
            .map((apk) => {
              const fileName = getFileName(apk);
              return {
                ...apk,
                fileName,
                displayName: getDisplayName(apk),
                env: getEnvironment(fileName),
                version: getVersion(apk),
                build: getBuildDate(apk),
              };
            })
            .sort((a, b) => a.displayName.localeCompare(b.displayName)),
        );
      } catch (error) {
        console.error("Error fetching APK data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // "All" plus whichever environments exist in the data (Prod first)
  const envTabs = useMemo(() => {
    const envs = [...new Set(apkData.map((apk) => apk.env).filter(Boolean))];
    envs.sort((a, b) =>
      a === "Prod" ? -1 : b === "Prod" ? 1 : a.localeCompare(b),
    );
    return ["All", ...envs];
  }, [apkData]);

  const visibleApps = useMemo(() => {
    const term = search.trim().toLowerCase();
    return apkData.filter(
      (apk) =>
        (envFilter === "All" || apk.env === envFilter) &&
        (!term ||
          apk.displayName.toLowerCase().includes(term) ||
          (apk.fileName || "").toLowerCase().includes(term)),
    );
  }, [apkData, search, envFilter]);

  return (
    <>
      <style>
        {`
          .dl-header {
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 16px;
            margin-bottom: 22px;
          }
          .dl-title {
            margin: 0;
            font-size: 26px;
            font-weight: 700;
            color: var(--color-primary);
            line-height: 1.2;
          }
          .dl-subtitle {
            margin: 4px 0 0;
            font-size: 13px;
            color: #6b7585;
          }
          .dl-controls {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-wrap: wrap;
          }
          /* Icon + divider come from the shared search-input style in theme.css */
          .dl-search input {
            width: 240px;
          }
          .dl-tabs {
            display: inline-flex;
            padding: 3px;
            gap: 2px;
            background: #eceef1;
            border-radius: 8px;
          }
          .dl-tab {
            height: 32px;
            padding: 0 14px;
            font-size: 12px;
            font-weight: 500;
            color: #4b5565;
            background: transparent;
            border: 0;
            border-radius: 6px;
            cursor: pointer;
            transition: background 0.15s ease, color 0.15s ease;
          }
          .dl-tab:hover {
            color: #1B2128;
          }
          .dl-tab.is-active {
            background: #ffffff;
            color: #1B2128;
            font-weight: 600;
            box-shadow: 0 1px 2px rgba(16, 24, 40, 0.1);
          }
          .dl-grid {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 12px;
          }
          @media (max-width: 1199px) {
            .dl-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
          }
          @media (max-width: 899px) {
            .dl-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          }
          @media (max-width: 575px) {
            .dl-grid { grid-template-columns: 1fr; }
            .dl-header { align-items: stretch; }
            .dl-title { font-size: 22px; }
            .dl-controls { width: 100%; }
            .dl-search { flex: 1 1 100%; }
            .dl-search input { width: 100%; }
            .dl-tabs { width: 100%; }
            .dl-tab { flex: 1; }
          }
          .dl-card {
            display: flex;
            flex-direction: column;
            min-width: 0;
            padding: 14px;
            text-align: left;
            background: #ffffff;
            border: 1px solid #eceef1;
            border-radius: 12px;
            transition: box-shadow 0.15s ease;
          }
          .dl-card:hover {
            box-shadow: 0 6px 18px rgba(16, 24, 40, 0.07);
          }
          .dl-card-top {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 14px;
          }
          .dl-icon {
            width: 30px;
            height: 30px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 6px;
            background: var(--color-primary);
            color: var(--btn-primary-bg);
            font-size: 15px;
          }
          .dl-badge {
            font-size: 10px;
            font-weight: 600;
            line-height: 1;
            padding: 4px 8px;
            border-radius: 999px;
            background: #fff3d6;
            color: #8a5a00;
          }
          .dl-badge.is-live {
            background: rgba(var(--color-primary-rgb), 0.1);
            color: var(--color-primary);
          }
          .dl-name {
            margin: 0;
            font-size: 14px;
            font-weight: 600;
            color: #1B2128;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .dl-meta {
            margin: 2px 0 12px;
            font-size: 11px;
            color: #8a94a6;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .dl-download {
            margin-top: auto;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 34px;
            border-radius: 6px;
            background: var(--btn-primary-bg);
            color: var(--btn-primary-text);
            font-size: 12px;
            font-weight: 600;
            text-decoration: none;
            transition: filter 0.15s ease;
          }
          .dl-download:hover {
            filter: brightness(0.94);
            color: var(--btn-primary-text);
          }
          .dl-empty {
            padding: 48px 16px;
            text-align: center;
            font-size: 13px;
            color: #6b7585;
            background: #ffffff;
            border: 1px solid #eceef1;
            border-radius: 12px;
          }
        `}
      </style>

      <div style={{ width: "100%" }}>
        <div className="content">
          <div className="page-content mt-3">
            <div className="dl-header mb-4">
              <div>
                <h3 className="dl-title">Downloads</h3>
              </div>

              <div className="dl-controls">
                <div className="dl-search">
                  <input
                    type="text"
                    placeholder="Search apps"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                {envTabs.length > 1 && (
                  <div className="dl-tabs" role="tablist">
                    {envTabs.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        role="tab"
                        aria-selected={envFilter === tab}
                        className={`dl-tab ${envFilter === tab ? "is-active" : ""}`}
                        onClick={() => setEnvFilter(tab)}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {isLoading ? (
              <TableLoader height={300} columns={4} />
            ) : visibleApps.length === 0 ? (
              <div className="dl-empty">
                {apkData.length === 0
                  ? "No apps available"
                  : "No apps match your search"}
              </div>
            ) : (
              <div className="dl-grid ">
                {visibleApps.map((apk, index) => {
                  const meta = [apk.version && `v${apk.version}`, apk.build]
                    .filter(Boolean)
                    .join(" · ");
                  return (
                    <div
                      key={apk.id || apk.fileName || index}
                      className="dl-card"
                    >
                      <div className="dl-card-top">
                        <span className="dl-icon">
                          <i className="fa-brands fa-android" />
                        </span>
                        {apk.env && (
                          <span
                            className={`dl-badge ${apk.env === "Live" ? "is-live" : ""}`}
                          >
                            {apk.env}
                          </span>
                        )}
                      </div>

                      <h6 className="dl-name" title={apk.displayName}>
                        {apk.displayName}
                      </h6>
                      <div className="dl-meta" title={apk.fileName}>
                        {meta || "—"}
                      </div>

                      <a
                        className="dl-download"
                        href={apk.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download
                      </a>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileAPKs;
